import type { LoginResponse } from 'src/requests/user';
import type { CreateWorkspaceResponse } from 'src/requests/workspace.requests';
import type { RNToWebAuthDataMessagePayload } from 'src/services/CollaborateNativeBridge/types';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

export type Section = 'communicate' | 'collaborate' | 'ai';

export type LocationData = {
  accessToken: string;
  userId: string;
  workspaceSlug: string;
  workspaceId: string;
  lastPath: string;
  sectionRecords?: Record<Section, string | null>;
};

export type TemporarySessionData = {
  accessToken: string;
  userId: string;
  workspaceSlug: string;
  workspaceId: string;
};

type StartSessionFromMobilePayload = RNToWebAuthDataMessagePayload & {
  lastPath: string;
};

interface LocationState {
  temporarySessionToken: string | null;
  locations: Record<string, LocationData>;
  masterToken: string;
  lastLocation: string | null;
  isAppCollaborate: boolean;
  addToHistory: (section: Section, path: string) => void;
  sessionStarted: (
    workspace: Pick<
      LocationData,
      'userId' | 'workspaceSlug' | 'accessToken' | 'workspaceId' | 'lastPath'
    >,
  ) => void;
  removeAllSessions: () => void;
  removeSession: (userId: string) => void;
}

export const appLocationState = create<LocationState>()(
  persist(
    immer((set) => ({
      temporarySessionToken: null,
      locations: {},
      masterToken: '',
      lastLocation: null,
      isAppCollaborate: false,
      addToHistory: (section, path) => {
        set((state) => {
          const currentSession = getCurrentSession();
          const userId = currentSession?.userId;
          if (!userId)
            throw new Error('Add to history failed: workspace not found');
          if (!state.locations[userId])
            throw new Error('Add to history failed: workspace not found');

          if (!state.locations[userId].sectionRecords)
            state.locations[userId].sectionRecords = {
              communicate: null,
              collaborate: null,
              ai: null,
            };

          const [accountIndex, ...rest] = path.split('/').filter(Boolean);
          if (accountIndex !== getSessionSlug(currentSession)) {
            throw new Error(
              'Attempting to add a path to a different workspace',
            );
          }
          const newPath = rest.join('/');
          state.lastLocation = userId;
          state.locations[userId].sectionRecords[section] = newPath;
          state.locations[userId].lastPath = newPath;
        });
      },
      sessionStarted(workspace) {
        set((state) => {
          if (!state.locations[workspace.userId])
            state.locations[workspace.userId] = {
              accessToken: workspace.accessToken,
              userId: workspace.userId,
              workspaceSlug: workspace.workspaceSlug,
              workspaceId: workspace.workspaceId,
              lastPath: workspace.lastPath || '',
            };

          state.locations[workspace.userId].accessToken = workspace.accessToken;
          state.locations[workspace.userId].workspaceSlug =
            workspace.workspaceSlug;
        });
      },
      removeAllSessions: () =>
        set(() => ({
          locations: {},
          lastLocation: null,
        })),
      removeSession: (userId: string) =>
        set((state) => {
          // @ts-expect-error
          state.locations[userId] = undefined;
          if (state.lastLocation === userId) {
            state.lastLocation = Object.keys(state.locations)[0] || null;
          }
        }),
    })),
    {
      name: 'app-location-state-storage',
      version: 3,
      getStorage: () => localStorage,
      partialize(state) {
        return {
          locations: state.locations,
          lastLocation: state.lastLocation,
          masterToken: state.masterToken,
          isAppCollaborate: state.isAppCollaborate,
        };
      },
    },
  ),
);

/**
 * Get the current session based on the workspace slug from the url
 * @returns the current session
 */
export const getCurrentSession = () => {
  const [accountIndex] = window.location.pathname.split('/').filter(Boolean);
  return getSessionByWorkspaceSlug(accountIndex)!;
};

/**
 * Get the last session based on the last location
 * @returns the last session
 */
export const getLastSession = () => {
  const { lastLocation } = appLocationState.getState();
  return lastLocation ? getSessionByUserId(lastLocation) : null;
};

export const startSessionFromLogin = ({
  user,
  accessTokens,
  accountToken,
}: LoginResponse) => {
  const { sessionStarted } = appLocationState.getState();

  if (!user.myUsers) {
    startTemporarySession(accessTokens[0].accessToken);
    return;
  }

  user.myUsers.forEach((user) => {
    sessionStarted({
      accessToken: accessTokens.find((token) => token._id === user._id)!
        .accessToken,
      userId: user._id,
      workspaceSlug: user.workspace.slug,
      workspaceId: user.workspace._id,
      lastPath: '',
    });
  });

  if (accountToken) {
    appLocationState.setState({
      masterToken: accountToken,
    });
  }
};

export const startTemporarySession = (token: string) => {
  appLocationState.setState((state) => {
    state.temporarySessionToken = token;
  });
};

export const getSessionByUserId = (userId: string) => {
  const { locations } = appLocationState.getState();
  return locations[userId];
};

export const getSessionByWorkspaceSlug = (workspaceSlug: string) => {
  const { locations } = appLocationState.getState();
  return Object.values(locations).find(
    (location) => getSessionSlug(location) === workspaceSlug,
  );
};

export const forceStartTemporarySession = () => {
  const { locations, temporarySessionToken } = appLocationState.getState();
  if (temporarySessionToken) return;

  const session = Object.values(locations)[0];
  if (!session) return;

  startTemporarySession(session.accessToken);
};

export const getSessionSlug = (session: LocationData) => {
  if (!session) return '';
  return (
    session.workspaceSlug ||
    session.workspaceId.substring(session.workspaceId.length - 6)
  );
};

export const addNewWorkspaceSession = ({
  workspace,
  accessTokens,
  newUser,
}: CreateWorkspaceResponse) => {
  const { sessionStarted } = appLocationState.getState();

  const newUserToken = accessTokens!.accessTokens.find(
    (i) => i._id === newUser._id,
  );

  sessionStarted({
    accessToken: newUserToken!.accessToken,
    userId: newUser._id,
    workspaceSlug: workspace.slug,
    workspaceId: workspace._id,
    lastPath: '',
  });
};

export const startSessionFromMobile = ({
  accessTokens,
  user,
  accountToken,
  lastLocation,
  lastPath,
}: StartSessionFromMobilePayload) => {
  const { sessionStarted } = appLocationState.getState();

  if (!user.myUsers) {
    startTemporarySession(accessTokens[0].accessToken);
    return;
  }

  user.myUsers.forEach((user) => {
    sessionStarted({
      accessToken: accessTokens.find((token) => token._id === user._id)!
        .accessToken,
      userId: user._id,
      workspaceSlug: user.workspace.slug,
      workspaceId: user.workspace._id,
      lastPath,
    });
  });

  appLocationState.setState({
    masterToken: accountToken,
    lastLocation,
    isAppCollaborate: true,
  });

  return user.workspace.slug;
};

export const useIsAppCollaborate = () =>
  appLocationState((state) => state.isAppCollaborate);
